<script setup lang="ts">
import { propsFactory, useModelValue } from "@soft-db/soft-utils";
import type { Numeric } from "d3";
import * as d3 from "d3";
import { computed, ref, useAttrs, type PropType } from "vue";
import type {
	AxisOptions,
	AxisScale,
	AxisSide,
	CartesianChartOptions,
	CartesianSerieOptions,
	CartesianSerieWithValue,
	DataPoint,
	LegendOptions,
	SVGContainerOptions,
	SVGPosition,
	ScaleMap,
	XYCursorOptions,
} from "../interfaces";
import { useCartesianSerieUtils } from "../use";
import { provideCartesianChart } from "../use/cartesian-chart";
import Axis from "./Axis.vue";
import LegendChart from "./Legend";
import SVGContainer from "./SVGContainer.vue";
import XYCursor from "./XYCursor.vue";

export type ClickEventPayload = Partial<{
	top: Numeric;
	right: Numeric;
	left: Numeric;
	bottom: Numeric;
}>;

const props = defineProps(
	propsFactory<CartesianChartOptions>()({
		axes: {
			type: Array as PropType<AxisOptions[]>,
			default: () => [],
		},
		layout: Object as PropType<SVGContainerOptions>,
		cursor: Object as PropType<XYCursorOptions>,
		legend: Object as PropType<LegendOptions>,
		series: Array as PropType<CartesianSerieOptions[]>,
		animation: Boolean,
		tooltip: Boolean,
		activeSerie: String,
	})
);

const emit = defineEmits<{
	(e: "click", payload: ClickEventPayload): void;
	(e: "dblclick", payload: ClickEventPayload): void;
	(e: "update:activeSerie", payload: string): void;
	(e: "zoom:top_axis", payload: Numeric[]): void;
	(e: "zoom:bottom_axis", payload: Numeric[]): void;
	(e: "zoom:left_axis", payload: Numeric[]): void;
	(e: "zoom:right_axis", payload: Numeric[]): void;
}>();

const attrs = useAttrs();

const graphContent = ref<HTMLDivElement>();

const legendPosition = computed(() => props.legend?.position);

const graphPanelStyle = computed(() => {
	return {
		"flex-flow": legendPosition.value === "right" ? "row" : "column",
	};
});

const legendRight = computed(() => {
	const { legend } = props;
	const position = legend?.position;
	return position === "right" || position === "float";
});

const legendFloating = computed(() => {
	return legendPosition.value === "float";
});

const filteredEvents = computed(() => {
	const events = Object.fromEntries(Object.entries(attrs).filter(([key]) => key.startsWith("on")));
	if (events.onClick) {
		delete events.onClick;
	}
	return events;
});

const scales = ref<ScaleMap>({});
const originalScale = ref<ScaleMap>({});

provideCartesianChart({ scales });

const updateScale = (side: AxisSide, scale: AxisScale) => {
	scales.value[side] = scale;
	scales.value = { ...scales.value };
};

const updateOriginalScale = (side: AxisSide, scale: AxisScale) => {
	originalScale.value[side] = scale;
	originalScale.value = { ...originalScale.value };
};

const updateRange = (side: AxisSide, range: Numeric[]) => {
	emit(`zoom:${side}_axis` as any, range);
};

const getScalesPositionFromCoordinates = (position: SVGPosition) => {
	const clickScalePosition: ClickEventPayload = {};

	for (const [side, scale] of Object.entries(scales.value)) {
		if (!scale) continue;

		let value: number;
		if (side === "left" || side === "right") {
			value = position.y;
		} else {
			value = position.x;
		}
		clickScalePosition[side] = scale.invert(value);
	}

	return clickScalePosition;
};

const clickHandler = (event: SVGPosition) => {
	emit("click", getScalesPositionFromCoordinates(event));
};

const dblClickHandler = (event: SVGPosition) => {
	emit("dblclick", getScalesPositionFromCoordinates(event));
};

const { searchPoint } = useCartesianSerieUtils();

const NO_VALUE = "---";
const seriesWithValue = computed<CartesianSerieWithValue[]>(() => {
	if (!props.series) return [];

	return props.series.map((serie) => {
		const x = props.cursor?.x?.value || 0;
		let y: string | number = NO_VALUE;

		let point: DataPoint | null = null;
		if (serie.valueResolver != null) {
			point = serie.valueResolver(x);
		} else {
			const searchResult = searchPoint(serie.data, x, props.legend?.searchType || "left");
			point = searchResult?.point || null;
		}

		const { format } = serie;

		if (point && point.y != null) {
			if (format) {
				if (typeof format === "function") {
					y = format(point.y);
				} else {
					y = d3.format(format)(point.y);
				}
			} else {
				y = point.y;
			}
		}

		const label = serie.label || "";
		const value = y;
		const metadata = point?.metadata;

		let unit: string | undefined;
		if (typeof serie.unit === "function") {
			unit = serie.unit(serie.id, x.valueOf(), point?.y ?? null);
		} else if (y !== NO_VALUE) {
			unit = serie.unit;
		}

		return {
			...serie,
			label,
			value,
			unit,
			metadata,
		};
	});
});

const activeSerieSync = useModelValue(props, "activeSerie", "");

const showTooltip = computed(() => {
	return props.tooltip;
});

const svgContainer = ref<InstanceType<typeof SVGContainer>>();

defineExpose({ svgContainer, scales, originalScale });
</script>

<template>
	<div class="relative flex h-full" :style="graphPanelStyle">
		<div ref="graphContent" class="relative flex-auto">
			<SVGContainer
				ref="svgContainer"
				:tooltip="showTooltip"
				v-bind="{ ...layout, ...filteredEvents }"
				@click="clickHandler"
				@dblclick="dblClickHandler"
			>
				<template #clip>
					<slot />
					<XYCursor v-bind="cursor" />
				</template>
				<Axis
					v-for="axis of axes"
					:key="axis.side"
					v-bind="axis"
					:animation="animation"
					@update:scale="(scale) => updateScale(axis.side, scale)"
					@update:originalScale="(scale) => updateOriginalScale(axis.side, scale)"
					@update:range="(range) => updateRange(axis.side, range)"
				/>
				<template #tooltip="{ x, y }">
					<slot name="tooltip" v-bind="getScalesPositionFromCoordinates({ x, y })" />
				</template>
			</SVGContainer>
		</div>
		<div
			v-if="legend && legend.visible"
			class="flex-initial"
			:class="{
				'flex-initial': true,
				'border-border bg-popover absolute right-0 mr-8 rounded-md border border-solid p-2':
					legendFloating,
				'mr-4': legendRight,
			}"
		>
			<LegendChart
				v-model:activeSerie="activeSerieSync"
				:right="legendRight"
				:series="seriesWithValue"
				:showValue="legend.showValue"
				v-bind="$attrs"
			>
				<template v-if="$slots['legend:item:label']" #item:label="{ serie }">
					<slot name="legend:item:label" :serie="serie" />
				</template>

				<template v-if="$slots['legend:item:value']" #item:value="{ serie }">
					<slot name="legend:item:value" :serie="serie" />
				</template>
			</LegendChart>
		</div>
	</div>
</template>
